import LecturerLayout from '../../components/LecturerLayout';
import React, { useEffect, useMemo, useState } from 'react';
import Swal from 'sweetalert2';
import axiosInstance from '../../utils/axiosInstance';
import { normalizeImagePath, getInitialsAvatar } from '../../utils/mediaUtils';
import { getImageURL } from '../../utils/api';

function to12h(time24) {
    if (!time24) return '';
    const [h, m] = time24.split(':');
    let hours = parseInt(h, 10);
    const modifier = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12 || 12;
    return `${hours}:${m} ${modifier}`;
}

export default function Attendance() {
    const today = new Date().toISOString().split('T')[0];
    const [selectedDate, setSelectedDate] = useState(today);
    const [schedules, setSchedules] = useState([]);
    const [loading, setLoading] = useState(false);
    
    // Modal states
    const [showModal, setShowModal] = useState(false);
    const [selectedSchedule, setSelectedSchedule] = useState(null);
    const [batches, setBatches] = useState([]);
    const [selectedBatchId, setSelectedBatchId] = useState(null);
    const [selectedStudentIds, setSelectedStudentIds] = useState(new Set());
    const [studentSearchTerm, setStudentSearchTerm] = useState('');
    const [presentSearchTerm, setPresentSearchTerm] = useState('');
    const [modalLoading, setModalLoading] = useState(false);
    const [currentStep, setCurrentStep] = useState(1);
    const [markingAttendance, setMarkingAttendance] = useState(false);
    const [viewingSummary, setViewingSummary] = useState(null);
    const [studentImages, setStudentImages] = useState({});
    const [loadingImages, setLoadingImages] = useState(new Set());

    // Other-events state
    const [otherEvents, setOtherEvents] = useState([]);
    const [otherEventsLoading, setOtherEventsLoading] = useState(false);
    const [viewingEventSummary, setViewingEventSummary] = useState(null);
    // 'schedule' | 'event' — which kind of attendance session is open in the modal
    const [attendanceMode, setAttendanceMode] = useState('schedule');
    const [selectedEvent, setSelectedEvent] = useState(null);

    const validateImageUrl = async (url) => {
        if (!url || url.startsWith('data:')) return Boolean(url);
        try {
            const response = await fetch(url, {
                method: 'HEAD',
                credentials: 'same-origin',
            });
            return response.ok;
        } catch (error) {
            console.warn('Image validation failed:', error);
            return false;
        }
    };

    useEffect(() => {
        if (selectedDate) {
            fetchSchedules(selectedDate);
            fetchOtherEvents(selectedDate);
        }
    }, [selectedDate]);

    const fetchOtherEvents = async (date) => {
        setOtherEventsLoading(true);
        try {
            const response = await axiosInstance.get('/admin/attendance/lecturer/other-events', {
                params: { date },
            });
            setOtherEvents(response.data?.events || []);
        } catch (error) {
            console.error('Error fetching other events:', error);
            setOtherEvents([]);
        } finally {
            setOtherEventsLoading(false);
        }
    };

    const fetchSchedules = async (date) => {
        setLoading(true);
        try {
            const response = await axiosInstance.get('/admin/attendance/lecturer/schedules', {
                params: { date },
                // Treat 304 as non-fatal if backend caches
                validateStatus: (status) => (status >= 200 && status < 300) || status === 304,
            });

            if (response.status === 304) {
                return;
            }

            setSchedules(response.data || []);
        } catch (error) {
            console.error('Error fetching schedules:', error?.response || error);
            const backendMessage = error?.response?.data?.message || error?.response?.data?.error;
            Swal.fire({
                title: 'Error',
                text: backendMessage || 'Failed to load schedules',
                icon: 'error',
            });
        } finally {
            setLoading(false);
        }
    };

    const openAttendanceModal = async (schedule) => {
        if (schedule.isAttendanceMarked) {
            Swal.fire({
                title: 'Already Marked',
                text: 'Attendance has already been marked for this class',
                icon: 'info',
            });
            return;
        }

        setSelectedSchedule(schedule);
        setShowModal(true);
        setModalLoading(true);
        setSelectedBatchId(null);
        setSelectedStudentIds(new Set());

        try {
            const response = await axiosInstance.get(`/admin/attendance/schedules/${schedule.scheduleId}/students`);
            const data = response?.data || {};
            setBatches(data.batches || []);
            
            // Always go directly to step 2, auto-select first batch if only one exists
            if (data.batches.length === 1) {
                setSelectedBatchId(data.batches[0].batchId);
            }
            setCurrentStep(2);
        } catch (error) {
            console.error('Error fetching students:', error);
            Swal.fire({
                title: 'Error',
                text: 'Failed to load students',
                icon: 'error',
            });
            setShowModal(false);
        } finally {
            setModalLoading(false);
        }
    };

    const closeModal = () => {
        setShowModal(false);
        setSelectedSchedule(null);
        setSelectedEvent(null);
        setAttendanceMode('schedule');
        setBatches([]);
        setSelectedBatchId(null);
        setSelectedStudentIds(new Set());
        setStudentSearchTerm('');
        setPresentSearchTerm('');
        setCurrentStep(1);
        setStudentImages({});
        setLoadingImages(new Set());
    };

    const openEventAttendanceModal = async (event) => {
        if (event.isAttendanceMarked) {
            Swal.fire({
                title: 'Already Marked',
                text: 'Attendance has already been marked for this event',
                icon: 'info',
            });
            return;
        }

        setAttendanceMode('event');
        setSelectedEvent(event);
        setShowModal(true);
        setModalLoading(true);
        setSelectedBatchId(null);
        setSelectedStudentIds(new Set());

        try {
            const response = await axiosInstance.get(`/admin/attendance/other-events/${event.eventId}/students`);
            const data = response?.data || {};
            setBatches(data.batches || []);
            if ((data.batches || []).length === 1) {
                setSelectedBatchId(data.batches[0].batchId);
            }
            setCurrentStep(2);
        } catch (error) {
            console.error('Error fetching event students:', error);
            Swal.fire({
                title: 'Error',
                text: 'Failed to load students',
                icon: 'error',
            });
            setShowModal(false);
        } finally {
            setModalLoading(false);
        }
    };

    const viewEventAttendanceSummary = async (eventId) => {
        if (!eventId) return;
        setViewingEventSummary(eventId);
        Swal.fire({
            title: 'Loading Summary',
            html: '<p class="text-sm text-gray-600">Fetching attendance details, please wait...</p>',
            allowOutsideClick: false,
            showConfirmButton: false,
            didOpen: () => { Swal.showLoading(); },
        });
        try {
            const response = await axiosInstance.get(`/admin/attendance/other-events/attendance/details/${eventId}`);
            const data = response?.data || {};
            const presentStudents = data.present || [];
            const absentStudents  = data.absent  || [];
            const summary = data.summary || {};
            const pct = Number.isFinite(summary.percentage) ? summary.percentage.toFixed(1) : '0.0';

            const ev = otherEvents.find((e) => e.eventId === eventId);
            const [presentList, absentList] = await Promise.all([
                buildSummaryListHtml(presentStudents, 'Present'),
                buildSummaryListHtml(absentStudents, 'Absent'),
            ]);

            Swal.fire({
                title: 'Attendance Summary',
                html: `
                    <div class="text-left">
                        <div class="bg-blue-50 rounded-lg p-4 mb-4">
                            <p class="font-semibold text-gray-900">${ev?.title || 'Event'}</p>
                            ${ev ? `<p class="text-sm text-gray-600 mt-1">${to12h(ev.startTime)} - ${to12h(ev.endTime)}</p>` : ''}
                            ${ev?.date ? `<p class="text-xs text-gray-500 mt-1">${ev.date}</p>` : ''}
                            ${ev?.classroomName ? `<p class="text-xs text-gray-500 mt-1">📍 ${ev.classroomName}</p>` : ''}
                        </div>
                        <div class="mb-4">
                            <div class="flex items-center justify-between text-xs mb-2">
                                <span class="text-gray-600 font-medium">Overall Attendance</span>
                                <span class="font-bold text-gray-900">${pct}%</span>
                            </div>
                            <div class="h-3 bg-gray-100 rounded-full overflow-hidden">
                                <div class="h-full bg-green-500 rounded-full" style="width:${pct}%"></div>
                            </div>
                        </div>
                        <div class="grid grid-cols-2 gap-3 mb-4">
                            <div class="bg-green-50 rounded-lg p-3 text-center">
                                <p class="text-2xl font-bold text-green-600">${summary.present ?? 0}</p>
                                <p class="text-xs text-gray-600 mt-1">Present</p>
                            </div>
                            <div class="bg-red-50 rounded-lg p-3 text-center">
                                <p class="text-2xl font-bold text-red-600">${summary.absent ?? 0}</p>
                                <p class="text-xs text-gray-600 mt-1">Absent</p>
                            </div>
                        </div>
                        ${presentStudents.length > 0 ? `
                            <div class="mb-4">
                                <h3 class="text-sm font-semibold text-gray-900 mb-2 flex items-center">
                                    <span class="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
                                    Present Students (${presentStudents.length})
                                </h3>
                                <div class="max-h-48 overflow-y-auto bg-gray-50 rounded-lg p-3">${presentList}</div>
                            </div>` : ''}
                        ${absentStudents.length > 0 ? `
                            <div>
                                <h3 class="text-sm font-semibold text-gray-900 mb-2 flex items-center">
                                    <span class="w-2 h-2 bg-red-500 rounded-full mr-2"></span>
                                    Absent Students (${absentStudents.length})
                                </h3>
                                <div class="max-h-48 overflow-y-auto bg-gray-50 rounded-lg p-3">${absentList}</div>
                            </div>` : ''}
                    </div>`,
                width: '650px',
                confirmButtonColor: '#4f46e5',
                confirmButtonText: 'Close',
                customClass: { popup: 'text-left' },
            });
        } catch (error) {
            console.error('Error fetching event attendance summary:', error);
            Swal.fire({
                title: 'Error',
                text: 'Failed to fetch attendance summary',
                icon: 'error',
            });
        } finally {
            setViewingEventSummary(null);
        }
    };

    const loadStudentImage = (studentId, imagePath, studentName) => {
        if (studentImages[studentId] || loadingImages.has(studentId)) {
            return;
        }

        const fallbackAvatar = getInitialsAvatar(studentName || '?');

        if (!imagePath) {
            setStudentImages(prev => ({ ...prev, [studentId]: fallbackAvatar }));
            return;
        }

        setLoadingImages(prev => new Set(prev).add(studentId));

        try {
            const finalSrc = imagePath || fallbackAvatar;
            setStudentImages(prev => ({ ...prev, [studentId]: finalSrc }));
        } catch (error) {
            console.error('Error loading student image:', error);
            setStudentImages(prev => ({ ...prev, [studentId]: fallbackAvatar }));
        } finally {
            setLoadingImages(prev => {
                const newSet = new Set(prev);
                newSet.delete(studentId);
                return newSet;
            });
        }
    };

    // Load images when students are loaded
    useEffect(() => {
        if (batches.length > 0 && selectedBatchId) {
            const batch = batches.find(b => b.batchId === selectedBatchId);
            if (batch) {
                batch.students.forEach(student => {
                    loadStudentImage(student.studentId, student.studentImage, student.studentName);
                });
            }
        }
    }, [batches, selectedBatchId]);

    const toggleStudentSelection = (studentId) => {
        setSelectedStudentIds(prev => {
            const newSet = new Set(prev);
            if (newSet.has(studentId)) {
                newSet.delete(studentId);
            } else {
                newSet.add(studentId);
            }
            return newSet;
        });
    };

    const handleMarkAttendance = async () => {
        // ── OTHER EVENT mode ──────────────────────────────────────────────────
        if (attendanceMode === 'event') {
            if (!selectedEvent) return;

            const result = await Swal.fire({
                title: 'Confirm Attendance',
                html: `
                    <div class="text-left">
                        <p class="mb-3">You are about to mark attendance for:</p>
                        <div class="bg-gray-50 rounded-lg p-4 mb-3">
                            <p class="font-semibold text-gray-900">${selectedEvent.title}</p>
                            <p class="text-sm text-gray-600 mt-1">${to12h(selectedEvent.startTime)} - ${to12h(selectedEvent.endTime)}</p>
                        </div>
                        <p class="text-sm">
                            <span class="font-semibold text-green-600">${selectedStudentIds.size}</span> students marked as <strong>Present</strong><br>
                            <span class="font-semibold text-red-600">${getCurrentBatchStudents().length - selectedStudentIds.size}</span> students will be marked as <strong>Absent</strong>
                        </p>
                    </div>
                `,
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#4f46e5',
                cancelButtonColor: '#6b7280',
                confirmButtonText: 'Yes, Mark Attendance',
                cancelButtonText: 'Cancel',
            });

            if (!result.isConfirmed) return;

            try {
                setMarkingAttendance(true);
                const response = await axiosInstance.post('/admin/attendance/other-events/attendance/mark', {
                    eventId: selectedEvent.eventId,
                    batchId: selectedBatchId,
                    presentStudentIds: Array.from(selectedStudentIds),
                });
                const data = response?.data || {};
                const savedEventId = selectedEvent.eventId;

                closeModal();
                await fetchOtherEvents(selectedDate);

                const summaryResult = await Swal.fire({
                    title: 'Success!',
                    html: `
                        <p>Attendance marked successfully!</p>
                        <p class="mt-2 text-sm">
                            <span class="text-green-600 font-semibold">${data.presentCount}</span> Present •
                            <span class="text-red-600 font-semibold">${data.absentCount}</span> Absent
                        </p>
                    `,
                    icon: 'success',
                    showCancelButton: true,
                    confirmButtonColor: '#4f46e5',
                    cancelButtonColor: '#6b7280',
                    confirmButtonText: 'View Summary',
                    cancelButtonText: 'Close',
                    showConfirmButton: true,
                });

                if (summaryResult.isConfirmed) {
                    await viewEventAttendanceSummary(savedEventId);
                }
            } catch (error) {
                console.error('Error marking event attendance:', error);
                Swal.fire({
                    title: 'Error',
                    text: error?.response?.data?.error || 'Failed to mark attendance',
                    icon: 'error',
                });
            } finally {
                setMarkingAttendance(false);
            }
            return;
        }

        // ── CLASS SCHEDULE mode ───────────────────────────────────────────────
        if (!selectedSchedule) return;

        const result = await Swal.fire({
            title: 'Confirm Attendance',
            html: `
                <div class="text-left">
                    <p class="mb-3">You are about to mark attendance for:</p>
                    <div class="bg-gray-50 rounded-lg p-4 mb-3">
                        <p class="font-semibold text-gray-900">${selectedSchedule.subjectFullName || selectedSchedule.subjectShortName}</p>
                        <p class="text-sm text-gray-600 mt-1">${to12h(selectedSchedule.startTime)} - ${to12h(selectedSchedule.endTime)}</p>
                    </div>
                    <p class="text-sm">
                        <span class="font-semibold text-green-600">${selectedStudentIds.size}</span> students marked as <strong>Present</strong><br>
                        <span class="font-semibold text-red-600">${getCurrentBatchStudents().length - selectedStudentIds.size}</span> students will be marked as <strong>Absent</strong>
                    </p>
                </div>
            `,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#4f46e5',
            cancelButtonColor: '#6b7280',
            confirmButtonText: 'Yes, Mark Attendance',
            cancelButtonText: 'Cancel',
        });

        if (!result.isConfirmed) return;

        try {
            setMarkingAttendance(true);
            const response = await axiosInstance.post('/admin/attendance/attendance/mark', {
                scheduleId: selectedSchedule.scheduleId,
                presentStudentIds: Array.from(selectedStudentIds),
            });

            const data = response?.data || {};

            closeModal();
            await fetchSchedules(selectedDate);

            // Show success message with View Summary button
            const summaryResult = await Swal.fire({
                title: 'Success!',
                html: `
                    <p>Attendance marked successfully!</p>
                    <p class="mt-2 text-sm">
                        <span class="text-green-600 font-semibold">${data.presentCount}</span> Present • 
                        <span class="text-red-600 font-semibold">${data.absentCount}</span> Absent
                    </p>
                `,
                icon: 'success',
                showCancelButton: true,
                confirmButtonColor: '#4f46e5',
                cancelButtonColor: '#6b7280',
                confirmButtonText: 'View Summary',
                cancelButtonText: 'Close',
                timer: undefined,
                showConfirmButton: true
            });

            // If user clicks "View Summary", show the detailed summary
            if (summaryResult.isConfirmed) {
                await showAttendanceSummary(data, selectedSchedule);
            }
        } catch (error) {
            console.error('Error marking attendance:', error);
            Swal.fire({
                title: 'Error',
                text: error instanceof Error ? error.message : 'Failed to mark attendance',
                icon: 'error',
            });
        }
        setMarkingAttendance(false);
    };

    const showAttendanceSummary = async (attendanceData, schedule) => {
        const presentStudents = batches
            .find(b => b.batchId === selectedBatchId)?.students
            .filter(s => selectedStudentIds.has(s.studentId)) || [];
        
        const absentStudents = batches
            .find(b => b.batchId === selectedBatchId)?.students
            .filter(s => !selectedStudentIds.has(s.studentId)) || [];

        const [presentList, absentList] = await Promise.all([
            buildSummaryListHtml(presentStudents, 'Present'),
            buildSummaryListHtml(absentStudents, 'Absent'),
        ]);

        Swal.fire({
            title: 'Attendance Summary',
            html: `
                <div class="text-left">
                    <div class="bg-blue-50 rounded-lg p-4 mb-4">
                        <p class="font-semibold text-gray-900">${schedule.subjectFullName || schedule.subjectShortName}</p>
                        <p class="text-sm text-gray-600 mt-1">${to12h(schedule.startTime)} - ${to12h(schedule.endTime)}</p>
                        <p class="text-xs text-gray-500 mt-1">${schedule.date}</p>
                    </div>
                    
                    <div class="grid grid-cols-2 gap-3 mb-4">
                        <div class="bg-green-50 rounded-lg p-3 text-center">
                            <p class="text-2xl font-bold text-green-600">${attendanceData.presentCount}</p>
                            <p class="text-xs text-gray-600 mt-1">Present</p>
                        </div>
                        <div class="bg-red-50 rounded-lg p-3 text-center">
                            <p class="text-2xl font-bold text-red-600">${attendanceData.absentCount}</p>
                            <p class="text-xs text-gray-600 mt-1">Absent</p>
                        </div>
                    </div>

                    ${presentStudents.length > 0 ? `
                        <div class="mb-4">
                            <h3 class="text-sm font-semibold text-gray-900 mb-2 flex items-center">
                                <span class="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
                                Present Students (${presentStudents.length})
                            </h3>
                            <div class="max-h-48 overflow-y-auto bg-gray-50 rounded-lg p-3">
                                ${presentList}
                            </div>
                        </div>
                    ` : ''}

                    ${absentStudents.length > 0 ? `
                        <div>
                            <h3 class="text-sm font-semibold text-gray-900 mb-2 flex items-center">
                                <span class="w-2 h-2 bg-red-500 rounded-full mr-2"></span>
                                Absent Students (${absentStudents.length})
                            </h3>
                            <div class="max-h-48 overflow-y-auto bg-gray-50 rounded-lg p-3">
                                ${absentList}
                            </div>
                        </div>
                    ` : ''}
                </div>
            `,
            width: '600px',
            confirmButtonColor: '#4f46e5',
            confirmButtonText: 'Close',
            customClass: {
                popup: 'text-left'
            }
        });
    };

    const viewAttendanceSummary = async (scheduleId) => {
        if (!scheduleId) return;

        setViewingSummary(scheduleId);
        Swal.fire({
            title: 'Loading Summary',
            html: '<p class="text-sm text-gray-600">Fetching attendance details, please wait...</p>',
            allowOutsideClick: false,
            showConfirmButton: false,
            didOpen: () => {
                Swal.showLoading();
            },
        });
        try {
            const response = await axiosInstance.get(`/admin/attendance/details/${scheduleId}`);
            const data = response?.data || {};

            const presentStudents = data.present || [];
            const absentStudents = data.absent || [];
            const summary = data.summary || {};
            const attendancePercentage = Number.isFinite(summary.percentage)
                ? summary.percentage.toFixed(1)
                : '0.0';

            const [presentList, absentList] = await Promise.all([
                buildSummaryListHtml(presentStudents, 'Present'),
                buildSummaryListHtml(absentStudents, 'Absent'),
            ]);

            // Find the schedule details from the loaded schedules list
            const schedule = schedules.find((s) => s.scheduleId === scheduleId);
            const subjectName = schedule
                ? (schedule.subjectFullName || schedule.subjectShortName || 'Class')
                : 'Class';
            const timeRange = schedule
                ? `${to12h(schedule.startTime)} - ${to12h(schedule.endTime)}`
                : '';
            const dateText = schedule?.date || '';
            const classroomText = schedule?.classroomName || '';
            const batchLabel = schedule
                ? (schedule.batchGroupId
                    ? (schedule.batchGroupWhatsappName || schedule.batchGroupName)
                    : (schedule.whatsappGroupName || schedule.batchInternalName))
                : '';

            Swal.fire({
                title: 'Attendance Summary',
                html: `
                    <div class="text-left">
                        <div class="bg-blue-50 rounded-lg p-4 mb-4">
                            <p class="font-semibold text-gray-900">${subjectName}</p>
                            ${timeRange ? `<p class="text-sm text-gray-600 mt-1">${timeRange}</p>` : ''}
                            ${dateText ? `<p class="text-xs text-gray-500 mt-1">${dateText}</p>` : ''}
                            ${classroomText ? `<p class="text-xs text-gray-500 mt-1">📍 ${classroomText}</p>` : ''}
                            ${batchLabel ? `<p class="text-xs text-gray-500 mt-1">👥 ${batchLabel}</p>` : ''}
                        </div>

                        <!-- Attendance Percentage Bar -->
                        <div class="mb-4">
                            <div class="flex items-center justify-between text-xs mb-2">
                                <span class="text-gray-600 font-medium">Overall Attendance</span>
                                <span class="font-bold text-gray-900">${attendancePercentage}%</span>
                            </div>
                            <div class="h-3 bg-gray-100 rounded-full overflow-hidden">
                                <div class="h-full bg-green-500 rounded-full" style="width: ${attendancePercentage}%"></div>
                            </div>
                        </div>
                        
                        <div class="grid grid-cols-2 gap-3 mb-4">
                            <div class="bg-green-50 rounded-lg p-3 text-center">
                                <p class="text-2xl font-bold text-green-600">${summary.present ?? 0}</p>
                                <p class="text-xs text-gray-600 mt-1">Present</p>
                            </div>
                            <div class="bg-red-50 rounded-lg p-3 text-center">
                                <p class="text-2xl font-bold text-red-600">${summary.absent ?? 0}</p>
                                <p class="text-xs text-gray-600 mt-1">Absent</p>
                            </div>
                        </div>

                        ${presentStudents.length > 0 ? `
                            <div class="mb-4">
                                <h3 class="text-sm font-semibold text-gray-900 mb-2 flex items-center">
                                    <span class="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
                                    Present Students (${presentStudents.length})
                                </h3>
                                <div class="max-h-48 overflow-y-auto bg-gray-50 rounded-lg p-3">
                                    ${presentList}
                                </div>
                            </div>
                        ` : ''}

                        ${absentStudents.length > 0 ? `
                            <div>
                                <h3 class="text-sm font-semibold text-gray-900 mb-2 flex items-center">
                                    <span class="w-2 h-2 bg-red-500 rounded-full mr-2"></span>
                                    Absent Students (${absentStudents.length})
                                </h3>
                                <div class="max-h-48 overflow-y-auto bg-gray-50 rounded-lg p-3">
                                    ${absentList}
                                </div>
                            </div>
                        ` : ''}
                    </div>
                `,
                width: '650px',
                confirmButtonColor: '#4f46e5',
                confirmButtonText: 'Close',
                customClass: {
                    popup: 'text-left'
                }
            });

        } catch (error) {
            console.error('Error fetching attendance summary:', error);
            Swal.fire({
                title: 'Error',
                text: error instanceof Error ? error.message : 'Failed to fetch attendance summary',
                icon: 'error',
            });
        } finally {
            setViewingSummary(null);
        }
    };

    const getCurrentBatchStudents = () => {
        if (!selectedBatchId) return [];
        const batch = batches.find(b => b.batchId === selectedBatchId);
        if (!batch) return [];
        
        // Filter out already selected students and sort by attendance percentage (high to low)
        return batch.students
            .filter(s => !selectedStudentIds.has(s.studentId))
            .sort((a, b) => b.attendancePercentage - a.attendancePercentage);
    };

    const getStudentImageUrl = (studentId, imagePath, studentName) => {
        // Return cached image if available
        if (studentImages[studentId]) {
            return studentImages[studentId];
        }

        // No stored image yet; return placeholder immediately when no path exists
        if (!imagePath) {
            return getInitialsAvatar(studentName || '?');
        }

        // Image path exists but fetch still in progress – signal caller to show loader
        return null;
    };

    const handleStudentImageError = (studentId, studentName) => {
        const fallback = getInitialsAvatar(studentName || '?');
        setStudentImages(prev => ({ ...prev, [studentId]: fallback }));
        return fallback;
    };

    // Build HTML list entries with ensured images fetched
    const buildSummaryListHtml = async (
        students,
        status
    ) => {
        const entries = await Promise.all(students.map(async (s) => {
            const imgUrl = s.studentImage || getInitialsAvatar(s.studentName || '?');
            const badge = status === 'Present'
                ? '<span class="text-xs px-2 py-1 bg-green-100 text-green-700 rounded">Present</span>'
                : '<span class="text-xs px-2 py-1 bg-red-100 text-red-700 rounded">Absent</span>';
            const info = `${s.registrationNumber || ''}${s.telephone ? ` • ${s.telephone}` : ''}`;
            return `
                <div class="flex items-center justify-between py-2 border-b border-gray-100">
                    <div class="flex items-center gap-3 min-w-0">
                        <img src="${getImageURL(imgUrl)}" class="h-8 w-8 rounded-full object-cover border border-gray-200 flex-shrink-0" alt="${s.studentName}" />
                        <div class="min-w-0">
                            <p class="font-medium text-gray-900 text-sm">${s.studentName}</p>
                            <p class="text-xs text-gray-600 truncate">${info}</p>
                        </div>
                    </div>
                    ${badge}
                </div>
            `;
        }));
        return entries.join('');
    };

    const hndCourseKey = 'HND in Computing';

    const normalizeKeyPart = (value) => (value || '').trim().toLowerCase();

    const buildScheduleKey = (schedule) => {
        const subjectName = normalizeKeyPart(schedule.subjectShortName || schedule.subjectFullName || 'Unknown Subject');
        const classroom = normalizeKeyPart(schedule.classroomName);
        const groupIdentifier = schedule.batchGroupId
            ? `${schedule.batchGroupId}|${schedule.batchGroupWhatsappName || ''}|${schedule.batchGroupName || ''}`
            : `${schedule.whatsappGroupName || ''}|${schedule.batchInternalName || ''}`;
        const normalizedGroup = normalizeKeyPart(groupIdentifier);

        return [
            subjectName,
            schedule.startTime || '',
            schedule.endTime || '',
            schedule.date || '',
            classroom,
            normalizedGroup,
        ].join('||');
    };

    const deduplicatedSchedules = useMemo(() => {
        const seenKeys = new Set();
        return schedules.filter((schedule) => {
            const key = buildScheduleKey(schedule);
            if (seenKeys.has(key)) {
                return false;
            }
            seenKeys.add(key);
            return true;
        });
    }, [schedules]);

    const groupSchedulesByCourse = (inputSchedules) => {
        const grouped = {};
        inputSchedules.forEach((schedule) => {
            const rawName = (schedule.courseName || 'Unknown Course').trim();
            const normalizedName = rawName || 'Unknown Course';
            const courseKey = normalizedName.toLowerCase().includes('hnd in computing')
                ? hndCourseKey
                : normalizedName;
            if (!grouped[courseKey]) {
                grouped[courseKey] = [];
            }
            grouped[courseKey].push(schedule);
        });
        return grouped;
    };

    const groupedSchedules = groupSchedulesByCourse(deduplicatedSchedules);
    const selectedBatchStudents = getCurrentBatchStudents();
    const isGroupSchedule = Boolean(selectedSchedule?.batchGroupId);
    const getBatchDisplayName = (batch) => {
        if (!batch) return 'Batch';
        return batch.batchInternalName || batch.batchName || `Batch #${batch.batchId}`;
    };
    
    // Get present students from ALL batches (not just the selected one)
    const presentStudents = batches
        .flatMap(batch => batch.students)
        .filter(s => selectedStudentIds.has(s.studentId));

    // Filtered lists based on search terms
    const filteredBatchStudents = selectedBatchStudents.filter((student) => {
        if (!studentSearchTerm.trim()) return true;
        const query = studentSearchTerm.toLowerCase();
        return (
            (student.studentName || '').toLowerCase().includes(query) ||
            (student.registrationNumber || '').toLowerCase().includes(query) ||
            (student.telephone || '').toLowerCase().includes(query)
        );
    });

    const filteredPresentStudents = presentStudents.filter((student) => {
        if (!presentSearchTerm.trim()) return true;
        const query = presentSearchTerm.toLowerCase();
        return (
            (student.studentName || '').toLowerCase().includes(query) ||
            (student.registrationNumber || '').toLowerCase().includes(query) ||
            (student.telephone || '').toLowerCase().includes(query)
        );
    });

    const selectedBatch = batches.find((batch) => batch.batchId === selectedBatchId);
    const selectedBatchTotalCount = selectedBatch ? (selectedBatch.students?.length || 0) : 0;
    const selectedBatchPresentCount = selectedBatch
        ? selectedBatch.students.filter((s) => selectedStudentIds.has(s.studentId)).length
        : 0;
    const attendanceProgress = selectedBatchTotalCount
        ? (selectedBatchPresentCount / selectedBatchTotalCount) * 100
        : 0;

    return (
        <LecturerLayout title="Mark Attendance">
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-8">
                {/* Header */}
                <div className="mb-8">
                    <h1 className="text-3xl font-bold text-gray-900">Mark Attendance</h1>
                    <p className="mt-2 text-sm text-gray-600">
                        View your scheduled classes and mark attendance
                    </p>
                </div>

                {/* Date Selector */}
                <div className="mb-6 rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
                    <div className="flex items-center gap-4">
                        <div className="flex items-center gap-2">
                            <svg className="h-5 w-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                            <label htmlFor="date" className="text-sm font-semibold text-gray-700">
                                Select Date
                            </label>
                        </div>
                        <input
                            type="date"
                            id="date"
                            value={selectedDate}
                            onChange={(e) => setSelectedDate(e.target.value)}
                            className="rounded-md border-gray-300 px-4 py-2 text-sm shadow-sm transition-colors focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500 focus:ring-offset-0"
                        />
                    </div>
                </div>

                {/* Loading State */}
                {loading && (
                    <div className="flex items-center justify-center py-12">
                        <div className="h-12 w-12 animate-spin rounded-full border-4 border-indigo-600 border-t-transparent"></div>
                    </div>
                )}

                {/* No Schedules */}
                {!loading && !otherEventsLoading && deduplicatedSchedules.length === 0 && otherEvents.length === 0 && (
                    <div className="rounded-lg bg-gray-50 p-8 text-center">
                        <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        <p className="mt-4 text-sm text-gray-600">No schedules or events found for this date</p>
                    </div>
                )}

                {/* Other Events Section */}
                {otherEvents.length > 0 && (
                    <div className="mb-8">
                        <div className="mb-4">
                            <h2 className="text-xl font-semibold text-gray-900">Other Events</h2>
                            <p className="mt-1 text-sm text-gray-600">{otherEvents.length} event(s) assigned to you</p>
                        </div>
                        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                            {otherEvents.map((event) => (
                                <div key={event.eventId} className="relative overflow-hidden rounded-lg border border-gray-200 bg-white p-6 shadow-sm transition-all hover:shadow-md">
                                    {/* Color stripe */}
                                    {event.eventTypeColor && (
                                        <div
                                            className="absolute left-0 top-0 h-full w-1 rounded-l-lg"
                                            style={{ backgroundColor: event.eventTypeColor }}
                                        />
                                    )}

                                    {/* Status Badge */}
                                    <div className="absolute right-4 top-4">
                                        {event.isAttendanceMarked ? (
                                            <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-3 py-1 text-xs font-medium text-green-800">
                                                <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                                </svg>
                                                Marked
                                            </span>
                                        ) : (
                                            <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-3 py-1 text-xs font-medium text-amber-800">
                                                <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                                </svg>
                                                Pending
                                            </span>
                                        )}
                                    </div>

                                    {/* Event Type badge */}
                                    {event.eventTypeName && (
                                        <div className="mb-2 pr-24">
                                            <span
                                                className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium"
                                                style={{
                                                    backgroundColor: event.eventTypeColor ? `${event.eventTypeColor}22` : '#f3f4f6',
                                                    color: event.eventTypeColor || '#374151',
                                                }}
                                            >
                                                {event.eventTypeName}
                                            </span>
                                        </div>
                                    )}

                                    {/* Title */}
                                    <div className="mb-4 pr-24">
                                        <h3 className="text-lg font-semibold text-gray-900">{event.title}</h3>
                                    </div>

                                    {/* Time */}
                                    <div className="mb-3 flex items-center gap-2 text-sm text-gray-600">
                                        <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        </svg>
                                        <span>{to12h(event.startTime)} - {to12h(event.endTime)}</span>
                                    </div>

                                    {/* Classroom */}
                                    {event.classroomName && (
                                        <div className="mb-3 flex items-center gap-2 text-sm text-gray-600">
                                            <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                                            </svg>
                                            <span>{event.classroomName}</span>
                                        </div>
                                    )}

                                    {/* Batches */}
                                    {event.batches && event.batches.length > 0 && (
                                        <div className="mb-4 flex items-center gap-2 text-sm text-gray-600">
                                            <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                                            </svg>
                                            <span>{event.batches.map((b) => b.whatsappGroupName || b.batchInternalName).join(', ')}</span>
                                        </div>
                                    )}

                                    {/* Attendance Stats (if marked) */}
                                    {event.isAttendanceMarked && (
                                        <div className="border-t border-gray-200 pt-4">
                                            <div className="mb-3">
                                                <div className="flex items-center justify-between text-xs mb-1">
                                                    <span className="text-gray-600">Attendance</span>
                                                    <span className="font-semibold text-gray-900">
                                                        {event.totalStudents > 0
                                                            ? ((event.presentCount / event.totalStudents) * 100).toFixed(1)
                                                            : '0.0'}%
                                                    </span>
                                                </div>
                                                <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                                                    <div
                                                        className="h-full bg-green-500 rounded-full transition-all"
                                                        style={{ width: `${event.totalStudents > 0 ? (event.presentCount / event.totalStudents) * 100 : 0}%` }}
                                                    />
                                                </div>
                                            </div>
                                            <div className="grid grid-cols-2 gap-2 text-xs mb-3">
                                                <div className="flex items-center justify-between rounded-md bg-green-50 px-2 py-1.5">
                                                    <span className="text-green-700">Present</span>
                                                    <span className="font-semibold text-green-900">{event.presentCount}</span>
                                                </div>
                                                <div className="flex items-center justify-between rounded-md bg-red-50 px-2 py-1.5">
                                                    <span className="text-red-700">Absent</span>
                                                    <span className="font-semibold text-red-900">{event.absentCount}</span>
                                                </div>
                                            </div>
                                            <button
                                                onClick={() => viewEventAttendanceSummary(event.eventId)}
                                                disabled={viewingEventSummary === event.eventId}
                                                className="flex w-full items-center justify-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:bg-blue-400"
                                            >
                                                {viewingEventSummary === event.eventId && (
                                                    <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none">
                                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V2C6.477 2 2 6.477 2 12h2zm2 5.291A7.962 7.962 0 014 12H2c0 3.042 1.135 5.824 3 7.938l1-2.647z" />
                                                    </svg>
                                                )}
                                                <span>{viewingEventSummary === event.eventId ? 'Loading...' : 'View Summary'}</span>
                                            </button>
                                        </div>
                                    )}

                                    {/* Mark Attendance Button */}
                                    {!event.isAttendanceMarked && (
                                        <button
                                            onClick={() => openEventAttendanceModal(event)}
                                            className="mt-4 w-full rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                                        >
                                            Mark Attendance
                                        </button>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Schedules Grouped by Course */}
                {!loading && deduplicatedSchedules.length > 0 && (
                    <div className="space-y-8">
                        {Object.entries(groupedSchedules).map(([courseName, courseSchedules]) => (
                            <div key={courseName}>
                                <div className="mb-4">
                                    <h2 className="text-xl font-semibold text-gray-900">{courseName}</h2>
                                    <p className="mt-1 text-sm text-gray-600">{courseSchedules.length} class(es)</p>
                                </div>
                                <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                                    {courseSchedules.map((schedule) => (
                                        <div key={schedule.scheduleId} className="relative overflow-hidden rounded-lg border border-gray-200 bg-white p-6 shadow-sm transition-all hover:shadow-md">
                                            {/* Status Badge */}
                                            <div className="absolute right-4 top-4">
                                                {schedule.isAttendanceMarked ? (
                                                    <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-3 py-1 text-xs font-medium text-green-800">
                                                        <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                                                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                                        </svg>
                                                        Marked
                                                    </span>
                                                ) : (
                                                    <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-3 py-1 text-xs font-medium text-amber-800">
                                                        <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                                                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                                        </svg>
                                                        Pending
                                                    </span>
                                                )}
                                            </div>

                                            {/* Subject Info */}
                                            <div className="mb-4 pr-24">
                                                <h3 className="text-lg font-semibold text-gray-900">
                                                    {schedule.subjectShortName || schedule.subjectFullName}
                                                </h3>
                                                {schedule.subjectShortName && schedule.subjectFullName && (
                                                    <p className="mt-1 text-sm text-gray-500">{schedule.subjectFullName}</p>
                                                )}
                                            </div>

                                            {/* Time */}
                                            <div className="mb-3 flex items-center gap-2 text-sm text-gray-600">
                                                <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                </svg>
                                                <span>{to12h(schedule.startTime)} - {to12h(schedule.endTime)}</span>
                                            </div>

                                            {/* Classroom */}
                                            {schedule.classroomName && (
                                                <div className="mb-3 flex items-center gap-2 text-sm text-gray-600">
                                                    <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                                                    </svg>
                                                    <span>{schedule.classroomName}</span>
                                                </div>
                                            )}

                                            {/* Batch */}
                                            <div className="mb-4 flex items-center gap-2 text-sm text-gray-600">
                                                <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                                                </svg>
                                                <span>
                                                    {schedule.batchGroupId 
                                                        ? (schedule.batchGroupWhatsappName || schedule.batchGroupName)
                                                        : (schedule.whatsappGroupName || schedule.batchInternalName)
                                                    }
                                                    {schedule.batchGroupId && (
                                                        <span className="ml-1 inline-flex items-center rounded-full bg-indigo-100 px-2 py-0.5 text-xs font-medium text-indigo-800">
                                                            Group
                                                        </span>
                                                    )}
                                                </span>
                                            </div>

                                            {/* Attendance Stats (if marked) */}
                                            {schedule.isAttendanceMarked && (
                                                <div className="border-t border-gray-200 pt-4">
                                                    {/* Attendance Percentage Progress Bar */}
                                                    <div className="mb-3">
                                                        <div className="flex items-center justify-between text-xs mb-1">
                                                            <span className="text-gray-600">Attendance</span>
                                                            <span className="font-semibold text-gray-900">
                                                                {((schedule.presentCount / (schedule.presentCount + schedule.absentCount)) * 100).toFixed(1)}%
                                                            </span>
                                                        </div>
                                                        <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                                                            <div 
                                                                className="h-full bg-green-500 rounded-full transition-all"
                                                                style={{ 
                                                                    width: `${((schedule.presentCount / (schedule.presentCount + schedule.absentCount)) * 100)}%` 
                                                                }}
                                                            ></div>
                                                        </div>
                                                    </div>
                                                    
                                                    <div className="grid grid-cols-2 gap-2 text-xs mb-3">
                                                        <div className="flex items-center justify-between rounded-md bg-green-50 px-2 py-1.5">
                                                            <span className="text-green-700">Present</span>
                                                            <span className="font-semibold text-green-900">{schedule.presentCount}</span>
                                                        </div>
                                                        <div className="flex items-center justify-between rounded-md bg-red-50 px-2 py-1.5">
                                                            <span className="text-red-700">Absent</span>
                                                            <span className="font-semibold text-red-900">{schedule.absentCount}</span>
                                                        </div>
                                                    </div>

                                                    {/* View Summary Button */}
                                                    <button
                                                        onClick={() => viewAttendanceSummary(schedule.scheduleId)}
                                                        className="flex w-full items-center justify-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:bg-blue-400"
                                                        disabled={viewingSummary === schedule.scheduleId}
                                                    >
                                                        {viewingSummary === schedule.scheduleId && (
                                                            <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none">
                                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V2C6.477 2 2 6.477 2 12h2zm2 5.291A7.962 7.962 0 014 12H2c0 3.042 1.135 5.824 3 7.938l1-2.647z"></path>
                                                            </svg>
                                                        )}
                                                        <span>{viewingSummary === schedule.scheduleId ? 'Loading Summary...' : 'View Summary'}</span>
                                                    </button>
                                                </div>
                                            )}

                                            {/* Mark Attendance Button */}
                                            {!schedule.isAttendanceMarked && (
                                                <button
                                                    onClick={() => openAttendanceModal(schedule)}
                                                    className="mt-4 w-full rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                                                >
                                                    Mark Attendance
                                                </button>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Fullscreen Attendance Modal */}
            {showModal && (
                <div className="fixed inset-0 z-50 overflow-hidden bg-gray-50">
                    {/* Modal Header */}
                    <div className="sticky top-0 z-10 border-b border-gray-200 bg-white shadow-sm">
                        <div className="mx-auto max-w-7xl px-6 py-4">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-4">
                                    <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-blue-600">
                                        <svg className="h-6 w-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                        </svg>
                                    </div>
                                    <div>
                                        <div className="flex items-center gap-2">
                                            <h2 className="text-xl font-bold text-gray-900">
                                                {attendanceMode === 'event'
                                                    ? (selectedEvent?.title || 'Event')
                                                    : (selectedSchedule?.subjectShortName || selectedSchedule?.subjectFullName || 'Subject')}
                                            </h2>
                                        </div>
                                        <p className="text-sm text-gray-500">Attendance Management</p>
                                        {attendanceMode === 'schedule' && selectedSchedule && (
                                            <div className="mt-1 flex items-center gap-4 text-sm text-gray-600">
                                                <span className="flex items-center gap-1">
                                                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                                    </svg>
                                                    {new Date(selectedSchedule.date).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                                                </span>
                                                <span className="flex items-center gap-1">
                                                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                    </svg>
                                                    {to12h(selectedSchedule.startTime)} - {to12h(selectedSchedule.endTime)}
                                                </span>
                                            </div>
                                        )}
                                        {attendanceMode === 'event' && selectedEvent && (
                                            <div className="mt-1 flex items-center gap-4 text-sm text-gray-600">
                                                <span className="flex items-center gap-1">
                                                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                                    </svg>
                                                    {new Date(selectedEvent.date).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                                                </span>
                                                <span className="flex items-center gap-1">
                                                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                    </svg>
                                                    {to12h(selectedEvent.startTime)} - {to12h(selectedEvent.endTime)}
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <button
                                    onClick={closeModal}
                                    className="rounded-md p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-600 focus:outline-none"
                                >
                                    <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Modal Content */}
                    <div className="mx-auto max-w-7xl px-6 py-8">
                        {modalLoading ? (
                            <div className="flex items-center justify-center py-12">
                                <div className="h-12 w-12 animate-spin rounded-full border-4 border-blue-600 border-t-transparent"></div>
                            </div>
                        ) : (
                            <>
                                {/* Step 1: Select Batch */}
                                {currentStep === 1 && (
                                    <div className="space-y-6">
                                        <div className="rounded-lg bg-white p-6 shadow-sm border border-gray-200">
                                            <h3 className="mb-4 text-lg font-semibold text-gray-900">Select Batch</h3>
                                            {isGroupSchedule && (
                                                <div className="mb-4 rounded-lg border-2 border-blue-500 bg-blue-50 p-4">
                                                    <div className="flex items-start gap-3">
                                                        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-600 flex-shrink-0">
                                                            <svg className="h-6 w-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                                                            </svg>
                                                        </div>
                                                        <div className="flex-1 min-w-0">
                                                            <div className="flex items-center gap-2 mb-1">
                                                                <h4 className="font-semibold text-blue-900 text-sm">Batch Group</h4>
                                                                <span className="inline-flex items-center rounded-full bg-blue-600 px-2 py-0.5 text-xs font-medium text-white">
                                                                    Group
                                                                </span>
                                                            </div>
                                                            <p className="font-medium text-gray-900 text-sm">
                                                                {selectedSchedule?.batchGroupWhatsappName || selectedSchedule?.batchGroupName}
                                                            </p>
                                                            <p className="text-xs text-blue-700 mt-1">
                                                                Combined batches: {batches.length} batch{batches.length !== 1 ? 'es' : ''}
                                                            </p>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                                                {batches.map((batch) => (
                                                    <button
                                                        key={batch.batchId}
                                                        onClick={() => {
                                                            setSelectedBatchId(batch.batchId);
                                                            setCurrentStep(2);
                                                        }}
                                                        className="group rounded-lg border-2 border-gray-300 bg-white p-6 text-left transition-all hover:border-blue-500 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                    >
                                                        <div className="flex items-center justify-between">
                                                            <h4 className="text-lg font-semibold text-gray-900 group-hover:text-blue-600">{getBatchDisplayName(batch)}</h4>
                                                            <svg className="h-6 w-6 text-gray-400 group-hover:text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                                            </svg>
                                                        </div>
                                                        <div className="mt-3 rounded-md bg-gray-100 px-3 py-1.5 text-sm text-gray-700">
                                                            {batch.students.length} Students
                                                        </div>
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Step 2: Mark Students */}
                                {currentStep === 2 && (
                                    <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
                                        {/* Left Section - Select Batch */}
                                        <div className="rounded-lg bg-white p-6 shadow-sm border border-gray-200">
                                            <h3 className="mb-4 text-base font-semibold text-gray-900">Select Batch</h3>
                                            
                                            {/* Batch Group Info - Only show if it's a grouped schedule */}
                                            {isGroupSchedule && (
                                                <div className="mb-4 rounded-lg border-2 border-blue-500 bg-blue-50 p-4">
                                                    <div className="flex items-start gap-3">
                                                        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-600 flex-shrink-0">
                                                            <svg className="h-6 w-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                                                            </svg>
                                                        </div>
                                                        <div className="flex-1 min-w-0">
                                                            <div className="flex items-center gap-2 mb-1">
                                                                <h4 className="font-semibold text-blue-900 text-sm">Batch Group</h4>
                                                                <span className="inline-flex items-center rounded-full bg-blue-600 px-2 py-0.5 text-xs font-medium text-white">
                                                                    Group
                                                                </span>
                                                            </div>
                                                            <p className="font-medium text-gray-900 text-sm">
                                                                {selectedSchedule?.batchGroupWhatsappName || selectedSchedule?.batchGroupName}
                                                            </p>
                                                            <p className="text-xs text-blue-700 mt-1">
                                                                Combined batches: {batches.length} batch{batches.length !== 1 ? 'es' : ''}
                                                            </p>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                            
                                            <div className="space-y-2">
                                                {batches.map((batch) => (
                                                    <button
                                                        key={batch.batchId}
                                                        onClick={() => {
                                                            setSelectedBatchId(batch.batchId);
                                                        }}
                                                        className={`w-full rounded-lg border p-4 text-left transition-all ${
                                                            selectedBatchId === batch.batchId
                                                                ? 'border-blue-600 bg-blue-50 shadow-sm'
                                                                : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm'
                                                        }`}
                                                    >
                                                        <div className="flex items-center justify-between">
                                                            <div>
                                                                <p className="font-medium text-gray-900 text-sm">{getBatchDisplayName(batch)}</p>
                                                                <p className="text-xs text-gray-600 mt-1">
                                                                    {batch.students.filter(s => !selectedStudentIds.has(s.studentId)).length} / {batch.students.length} Students
                                                                </p>
                                                            </div>
                                                            {selectedBatchId === batch.batchId && (
                                                                <div className="flex h-5 w-5 items-center justify-center rounded-full bg-blue-600">
                                                                    <svg className="h-3 w-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                                                                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                                                    </svg>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </button>
                                                ))}
                                            </div>
                                        </div>

                                        {/* Middle Section - Students List */}
                                        {selectedBatchId && (
                                            <div className="rounded-lg bg-white border border-gray-200 shadow-sm">
                                            <div className="border-b border-gray-200 bg-gray-50 px-6 py-4">
                                                <div className="flex items-center justify-between mb-3">
                                                    <h3 className="text-base font-semibold text-gray-900">
                                                        {getBatchDisplayName(batches.find(b => b.batchId === selectedBatchId))} Students
                                                    </h3>
                                                    <div className="rounded-md bg-white border border-gray-200 px-3 py-1 text-xs font-semibold text-gray-700">
                                                        {selectedBatchStudents.length} Students
                                                    </div>
                                                </div>

                                                {/* Search Box */}
                                                <input
                                                    type="text"
                                                    placeholder="Search students..."
                                                    value={studentSearchTerm}
                                                    onChange={(e) => setStudentSearchTerm(e.target.value)}
                                                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                />
                                            </div>

                                            {/* Students List */}
                                            <div className="p-4 space-y-2 max-h-[calc(100vh-400px)] overflow-y-auto">
                                                {filteredBatchStudents.length > 0 ? (
                                                    filteredBatchStudents.map((student) => {
                                                        const resolvedImageUrl = getStudentImageUrl(student.studentId, student.studentImage, student.studentName);
                                                        const fallbackAvatar = getInitialsAvatar(student.studentName || '?');
                                                        const showImageLoader = Boolean(student.studentImage && (!resolvedImageUrl || loadingImages.has(student.studentId)));

                                                        return (
                                                            <div
                                                                key={student.studentId}
                                                                onClick={() => toggleStudentSelection(student.studentId)}
                                                                className="group cursor-pointer rounded-lg border border-gray-200 p-3 transition-all hover:border-gray-300 hover:shadow-md bg-white"
                                                            >
                                                                <div className="flex items-center gap-3">
                                                                    <div className="relative h-12 w-12">
                                                                        {showImageLoader ? (
                                                                            <div className="flex h-12 w-12 items-center justify-center rounded-lg border border-gray-200 bg-gray-100">
                                                                                <svg className="h-5 w-5 animate-spin text-gray-500" viewBox="0 0 24 24" fill="none">
                                                                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V2C6.477 2 2 6.477 2 12h2zm2 5.291A7.962 7.962 0 014 12H2c0 3.042 1.135 5.824 3 7.938l1-2.647z"></path>
                                                                                </svg>
                                                                            </div>
                                                                        ) : (
                                                                            <img
                                                                                src={getImageURL(student.studentImage) || fallbackAvatar}
                                                                                alt={student.studentName}
                                                                                className="h-12 w-12 rounded-lg object-cover border border-gray-200"
                                                                                onError={(event) => {
                                                                                    const fallback = handleStudentImageError(student.studentId, student.studentName);
                                                                                    if (event.currentTarget.src !== fallback) {
                                                                                        event.currentTarget.src = fallback;
                                                                                    }
                                                                                }}
                                                                            />
                                                                        )}
                                                                    </div>
                                                                    <div className="flex-1 min-w-0">
                                                                        <p className="font-medium text-gray-900 text-sm">
                                                                            {student.studentName}
                                                                        </p>
                                                                        <div className="flex items-center gap-2 mt-0.5">
                                                                            <p className="text-xs text-gray-600 truncate">
                                                                                {student.registrationNumber}
                                                                            </p>
                                                                            {student.registrationStatus && (
                                                                                <span className={`text-xs px-1.5 py-0.5 rounded ${
                                                                                    student.registrationStatus === 'active' 
                                                                                        ? 'bg-green-100 text-green-700' 
                                                                                        : 'bg-gray-100 text-gray-700'
                                                                                }`}>
                                                                                    {student.registrationStatus}
                                                                                </span>
                                                                            )}
                                                                        </div>
                                                                        {student.telephone && (
                                                                            <p className="text-xs text-gray-500 mt-0.5">
                                                                                📞 {student.telephone}
                                                                            </p>
                                                                        )}
                                                                    </div>
                                                                    <button className="flex-shrink-0 rounded-md border border-green-600 bg-white px-3 py-1.5 text-xs font-medium text-green-600 hover:bg-green-50 transition-colors">
                                                                        Present
                                                                    </button>
                                                                </div>
                                                            {/* Attendance Percentage Bar */}
                                                            {attendanceMode !== 'event' && (
                                                            <div className="mt-2 flex items-center gap-2">
                                                                <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                                                                    <div
                                                                        className={`h-full ${
                                                                            student.attendancePercentage >= 75 ? 'bg-green-500' :
                                                                            student.attendancePercentage >= 50 ? 'bg-yellow-500' :
                                                                            'bg-red-500'
                                                                        }`}
                                                                        style={{ width: `${student.attendancePercentage}%` }}
                                                                    ></div>
                                                                </div>
                                                                <span className="text-xs font-medium text-gray-600">
                                                                    {student.attendancePercentage}%
                                                                </span>
                                                            </div>
                                                            )}
                                                        </div>
                                                        );
                                                    })
                                                ) : (
                                                    <div className="py-12 text-center">
                                                        <svg className="mx-auto h-12 w-12 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                                                        </svg>
                                                        <p className="mt-2 text-sm text-gray-500">All students marked as present</p>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        )}

                                        {/* Right Section - Present Students */}
                                        {selectedBatchId && (
                                            <div className="rounded-lg bg-white border border-gray-200 shadow-sm">
                                                <div className="border-b border-gray-200 bg-gray-50 px-6 py-4">
                                                    <div className="flex items-center justify-between mb-3">
                                                        <h3 className="text-base font-semibold text-gray-900">Present Students</h3>
                                                        <div className="rounded-md bg-green-600 px-3 py-1 text-xs font-semibold text-white">
                                                            {selectedStudentIds.size} Present
                                                        </div>
                                                    </div>

                                                    {/* Attendance Percentage Progress Bar */}
                                                    <div className="mb-3">
                                                        <div className="flex items-center justify-between text-xs mb-1">
                                                            <span className="text-gray-600">Attendance</span>
                                                            <span className="font-semibold text-gray-900">
                                                                {attendanceProgress.toFixed(1)}%
                                                            </span>
                                                        </div>
                                                        <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                                                            <div
                                                                className="h-full bg-green-500 rounded-full transition-all"
                                                                style={{ width: `${attendanceProgress}%` }}
                                                            ></div>
                                                        </div>
                                                        <p className="mt-1 text-[11px] text-gray-500">
                                                            {selectedBatchPresentCount} of {selectedBatchTotalCount} students marked present
                                                        </p>
                                                    </div>

                                                    {/* Search Box */}
                                                    <input
                                                        type="text"
                                                        placeholder="Search present students..."
                                                        value={presentSearchTerm}
                                                        onChange={(e) => setPresentSearchTerm(e.target.value)}
                                                        className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                    />
                                                </div>

                                            {/* Present Students List */}
                                            <div className="p-4 space-y-2 max-h-[calc(100vh-400px)] overflow-y-auto">
                                                {filteredPresentStudents.length > 0 ? (
                                                    filteredPresentStudents.map((student) => {
                                                        const resolvedImageUrl = getStudentImageUrl(student.studentId, student.studentImage, student.studentName);
                                                        const fallbackAvatar = getInitialsAvatar(student.studentName || '?');
                                                        const showImageLoader = Boolean(student.studentImage && (!resolvedImageUrl || loadingImages.has(student.studentId)));
                                                        const studentAttendance = Number.isFinite(student.attendancePercentage)
                                                            ? Math.max(0, Math.min(100, student.attendancePercentage))
                                                            : 0;

                                                        return (
                                                            <div
                                                                key={student.studentId}
                                                                className="flex flex-col gap-2 rounded-lg border border-green-200 bg-green-50 p-3"
                                                            >
                                                                <div className="flex items-center gap-3">
                                                                    <div className="relative h-12 w-12">
                                                                        {showImageLoader ? (
                                                                            <div className="flex h-12 w-12 items-center justify-center rounded-lg border border-green-200 bg-green-100">
                                                                                <svg className="h-5 w-5 animate-spin text-green-600" viewBox="0 0 24 24" fill="none">
                                                                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V2C6.477 2 2 6.477 2 12h2zm2 5.291A7.962 7.962 0 014 12H2c0 3.042 1.135 5.824 3 7.938l1-2.647z"></path>
                                                                                </svg>
                                                                            </div>
                                                                        ) : (
                                                                            <img
                                                                                src={getImageURL(student.studentImage) || fallbackAvatar}
                                                                                alt={student.studentName}
                                                                                className="h-12 w-12 rounded-lg object-cover border border-green-300"
                                                                                onError={(event) => {
                                                                                    const fallback = handleStudentImageError(student.studentId, student.studentName);
                                                                                    if (event.currentTarget.src !== fallback) {
                                                                                        event.currentTarget.src = fallback;
                                                                                    }
                                                                                }}
                                                                            />
                                                                        )}
                                                                    </div>
                                                                    <div className="flex-1 min-w-0">
                                                                        <p className="font-medium text-gray-900 text-sm">{student.studentName}</p>
                                                                        <div className="flex items-center gap-2 mt-0.5">
                                                                            <p className="text-xs text-gray-600 truncate">
                                                                                {student.registrationNumber}
                                                                            </p>
                                                                            {student.registrationStatus && (
                                                                                <span className={`text-xs px-1.5 py-0.5 rounded ${
                                                                                    student.registrationStatus === 'active' 
                                                                                        ? 'bg-green-200 text-green-800' 
                                                                                        : 'bg-gray-200 text-gray-700'
                                                                                }`}>
                                                                                    {student.registrationStatus}
                                                                                </span>
                                                                            )}
                                                                        </div>
                                                                        {student.telephone && (
                                                                            <p className="text-xs text-gray-600 mt-0.5">
                                                                                📞 {student.telephone}
                                                                            </p>
                                                                        )}
                                                                    </div>
                                                                    <button
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            toggleStudentSelection(student.studentId);
                                                                        }}
                                                                        className="flex-shrink-0 rounded-full p-1.5 hover:bg-red-100 transition-colors"
                                                                        title="Remove from present"
                                                                    >
                                                                        <svg className="h-4 w-4 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                                                        </svg>
                                                                    </button>
                                                                </div>

                                                                {/* Student Attendance Percentage Bar */}
                                                                {attendanceMode !== 'event' && (
                                                                <div className="flex items-center gap-2 mt-1">
                                                                    <div className="flex-1 h-1.5 bg-green-100 rounded-full overflow-hidden">
                                                                        <div
                                                                            className={`h-full ${
                                                                                studentAttendance >= 75 ? 'bg-green-600' :
                                                                                studentAttendance >= 50 ? 'bg-yellow-500' :
                                                                                'bg-red-500'
                                                                            }`}
                                                                            style={{ width: `${studentAttendance}%` }}
                                                                        ></div>
                                                                    </div>
                                                                    <span className="text-xs font-medium text-gray-700">
                                                                        {studentAttendance}%
                                                                    </span>
                                                                </div>
                                                                )}
                                                            </div>
                                                        );
                                                    })
                                                ) : (
                                                    <div className="py-12 text-center">
                                                        <svg className="mx-auto h-12 w-12 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                                                        </svg>
                                                        <p className="mt-2 text-sm text-gray-500">No students marked as present</p>
                                                    </div>
                                                )}
                                            </div>

                                            {/* Save Button */}
                                            <div className="border-t border-gray-200 p-4">
                                                <button
                                                    onClick={handleMarkAttendance}
                                                    disabled={selectedStudentIds.size === 0}
                                                    className="w-full rounded-lg bg-blue-600 px-4 py-3 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-blue-600 transition-colors shadow-sm"
                                                >
                                                    Save Attendance ({selectedStudentIds.size} students)
                                                </button>
                                            </div>
                                        </div>
                                        )}
                                    </div>
                                )}

                                {/* Step 3: Review (Keep existing or remove if not needed) */}
                                {currentStep === 3 && (
                                    <div>
                                        <div className="mb-6 flex items-center justify-between">
                                            <h3 className="text-lg font-semibold text-gray-900">Review Attendance</h3>
                                            <button
                                                onClick={() => setCurrentStep(2)}
                                                className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                                            >
                                                Back
                                            </button>
                                        </div>

                                        {/* Summary Cards */}
                                        <div className="mb-6 grid grid-cols-1 gap-4 md:grid-cols-3">
                                            <div className="rounded-lg bg-green-50 p-6 border border-green-200">
                                                <p className="text-sm text-green-700">Present</p>
                                                <p className="mt-2 text-3xl font-bold text-green-900">{selectedStudentIds.size}</p>
                                            </div>
                                            <div className="rounded-lg bg-red-50 p-6 border border-red-200">
                                                <p className="text-sm text-red-700">Absent</p>
                                                <p className="mt-2 text-3xl font-bold text-red-900">{selectedBatchStudents.length - selectedStudentIds.size}</p>
                                            </div>
                                            <div className="rounded-lg bg-gray-50 p-6 border border-gray-200">
                                                <p className="text-sm text-gray-700">Total Students</p>
                                                <p className="mt-2 text-3xl font-bold text-gray-900">{selectedBatchStudents.length}</p>
                                            </div>
                                        </div>

                                        {/* Submit Button */}
                                        <div className="flex justify-end gap-3">
                                            <button
                                                onClick={closeModal}
                                                className="rounded-md border border-gray-300 bg-white px-6 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50"
                                            >
                                                Cancel
                                            </button>
                                            <button
                                                onClick={handleMarkAttendance}
                                                className="flex items-center gap-2 rounded-md bg-blue-600 px-6 py-3 text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:bg-blue-400"
                                                disabled={markingAttendance}
                                            >
                                                {markingAttendance && (
                                                    <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none">
                                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V2C6.477 2 2 6.477 2 12h2zm2 5.291A7.962 7.962 0 014 12H2c0 3.042 1.135 5.824 3 7.938l1-2.647z"></path>
                                                    </svg>
                                                )}
                                                <span>{markingAttendance ? 'Marking...' : 'Mark Attendance'}</span>
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>
            )}
        </LecturerLayout>
    );
}
